import CustomerUpscale from "./CustomerUpscale";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import classNames from "classnames";
import Cropper from "react-easy-crop";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { Tooltip } from "react-tippy";
import axios from "axios";
import { ReactCompareSlider } from "react-compare-slider";
import Slider, { Range } from "rc-slider";
import { isMobile } from "react-device-detect";
import Modal from "components/shared/ScopedModal";
import { RadioGroup } from "@headlessui/react";
import cloudinary from "cloudinary-core";
import CurrencyDisplay from "components/helpers/CurrencyDisplay";
import ShippingEstimate from "components/cart/ShippingEstimate";
import {
  CUSTOM_SIZE_DEFAULT_UNIT,
  CUSTOM_SIZE_MAX_MM,
  CUSTOM_SIZE_UNIT_OPTIONS,
  flipCustomSizeDimensions,
  getCustomSizeValidationState,
  resolveCombinedLimitMm,
} from "components/frame-designer/custom_size/customSizeHelpers";
import {
  buildCropRatioErrorProps,
  captureSentryMessage,
  getCurrentCropRatioDetails,
  cropRatioMatches,
  cropRatioMismatchMessage,
  cropCoordinateSpaceWidth,
  displayImageSize,
  resolvePreviewSize,
  trackEvent,
} from "components/frame-designer/utils/cropRatioValidation";
import {
  inchesToMm,
  largestAvailableASize,
  maxLongestMmOk,
  resolveUpscaleTestGroup,
  trackUpscaleExposure,
  upscaleEligible,
  upscaleEventContext,
  upscaleTreatmentActive,
} from "./upscaleTracking";

const PRINT_TAB_INDEXES = {
  recommended: 0,
  square: 1,
  original: 2,
  custom: 3,
};

const WARM_PLASTER = "#E6E1D9";
const CHARCOAL = "#2B2B2A";
const EFFECT_FILTERS = ["ukulele", "audrey", "peacock", "primavera", "sonnet"];

export const upscaleUnlockCopy = (sizes = []) => {
  const count = (sizes || []).filter((size) => size.unlockable).length;

  if (count === 0) {
    return {
      title: "Want a sharper print?",
      subtitle: "Our AI Upscaler adds detail for crisper, larger prints.",
      count: 0,
    };
  }
  return {
    title: "Want it bigger? Upscale to unlock bigger sizes",
    subtitle: "Our AI Upscaler adds detail so larger sizes print sharp.",
    count,
  };
};

// upscale = { scale, shortestPrintLength } from the server's capped upscale.
export const fitsAfterUpscale = ({
  upscale,
  shortInches,
  longInches,
  longEdgePx,
  dpi,
}) => {
  if (!upscale?.scale || !upscale?.shortestPrintLength) return false;
  if (shortInches > upscale.shortestPrintLength) return false;
  if (longInches == null) return true;
  return longInches <= (longEdgePx * upscale.scale) / dpi;
};

// How far the crop centre sits from the image centre, as a percentage of the image.
export const cropOffsetPct = (croppedArea) => {
  if (!croppedArea) return null;
  const { x, y, width, height } = croppedArea;
  if (![x, y, width, height].every(Number.isFinite)) return null;
  return Math.round(
    Math.max(Math.abs(x + width / 2 - 50), Math.abs(y + height / 2 - 50)),
  );
};

export const buildRatioSelectionState = ({ cropperReady, ratio, updates }) => ({
  ...updates,
  ratio,
  pendingRatio: cropperReady ? null : ratio,
});

export const customPrintTabEnabled = ({ isPro, forceCustomTab = false }) =>
  forceCustomTab || isPro;

const NEAR_SQUARE_MIN_RATIO = 0.9;

export const isNearlySquare = (ratio) => {
  if (!Number.isFinite(ratio) || ratio <= 0) return false;
  return Math.min(ratio, 1 / ratio) >= NEAR_SQUARE_MIN_RATIO - 1e-9;
};

export const getTabIndexFromURL = (
  search,
  { customTabAllowed, sourceRatio } = {},
) => {
  const urlParams = new URLSearchParams(search);
  const tabParam = urlParams.get("tab");

  switch (tabParam) {
    case "recommended":
      return PRINT_TAB_INDEXES.recommended;
    case "square":
      return PRINT_TAB_INDEXES.square;
    case "original":
      return PRINT_TAB_INDEXES.original;
    case "custom":
      return customTabAllowed
        ? PRINT_TAB_INDEXES.custom
        : PRINT_TAB_INDEXES.recommended;
    default:
      return isNearlySquare(sourceRatio)
        ? PRINT_TAB_INDEXES.square
        : PRINT_TAB_INDEXES.recommended;
  }
};

export const getTabNameFromIndex = (index) => {
  switch (index) {
    case PRINT_TAB_INDEXES.recommended:
      return "recommended";
    case PRINT_TAB_INDEXES.square:
      return "square";
    case PRINT_TAB_INDEXES.original:
      return "original";
    case PRINT_TAB_INDEXES.custom:
      return "custom";
    default:
      return "recommended";
  }
};

export const shouldFetchTabSizes = (tabIndex) =>
  tabIndex !== PRINT_TAB_INDEXES.custom;

const validUrlDimension = (value) =>
  typeof value === "string" && /^\d+(\.\d+)?$/.test(value) && Number(value) > 0;

export const getCustomSizeFromURL = (search) => {
  const urlParams = new URLSearchParams(search);

  if (urlParams.get("tab") !== "custom") {
    return { width: "", height: "" };
  }

  const width = urlParams.get("width_mm") || "";
  const height = urlParams.get("height_mm") || "";

  return {
    width: validUrlDimension(width) ? width : "",
    height: validUrlDimension(height) ? height : "",
  };
};

export const getCropValidationResult = ({
  croppedArea,
  croppedAreaPixels,
  ratio,
  printSize,
  requirePrintSize = true,
  previewWidth,
  previewHeight,
  imageWidth,
  imageHeight,
}) => {
  if (!croppedAreaPixels) {
    return {
      code: "missing_crop_data",
      errorMessage:
        "Crop data is not available. Please try refreshing the page.",
    };
  }

  if (requirePrintSize && !printSize) {
    return {
      code: "missing_print_size",
      errorMessage: "Please select a print size before proceeding.",
    };
  }

  if (!ratio || isNaN(ratio) || ratio <= 0) {
    return {
      code: "invalid_ratio",
      errorMessage:
        "Print size ratio is not available. Please try selecting a different print size or refreshing the page.",
    };
  }

  const hasNaN =
    isNaN(croppedAreaPixels.x) ||
    isNaN(croppedAreaPixels.y) ||
    isNaN(croppedAreaPixels.width) ||
    isNaN(croppedAreaPixels.height);

  if (hasNaN) {
    return {
      code: "invalid_crop_data",
      errorMessage:
        "Invalid crop data detected. Please try refreshing the page and adjusting your crop again.",
    };
  }

  const { currentCropRatio, ratioSource } = getCurrentCropRatioDetails({
    croppedArea,
    croppedAreaPixels,
    previewWidth,
    previewHeight,
    imageWidth,
    imageHeight,
  });

  if (!Number.isFinite(currentCropRatio) || currentCropRatio <= 0) {
    return {
      code: "invalid_crop_ratio",
      errorMessage:
        "Crop ratio could not be calculated. Please try refreshing the page and adjusting your crop again.",
    };
  }

  if (!cropRatioMatches(currentCropRatio, ratio)) {
    return {
      code: "ratio_mismatch",
      errorMessage: cropRatioMismatchMessage(currentCropRatio, ratio),
      currentCropRatio,
      ratioSource,
    };
  }

  return {
    code: null,
    errorMessage: null,
    currentCropRatio,
    ratioSource,
  };
};

export const isContinueDisabled = ({
  tabIndex,
  loading,
  dpiState,
  cropperReady,
  saving,
  ratio,
  printSize,
  effectsAccordionOpen,
  customSizeValid,
}) => {
  const hasActiveSize =
    tabIndex === PRINT_TAB_INDEXES.custom ? customSizeValid : !!printSize;

  return (
    loading ||
    dpiState === "too-low" ||
    !cropperReady ||
    saving ||
    !ratio ||
    isNaN(ratio) ||
    ratio <= 0 ||
    !hasActiveSize ||
    effectsAccordionOpen
  );
};

export const buildDesignerRedirectUrl = ({
  frameDesignerPath,
  width,
  height,
  unit,
  imageKey,
  paperTypeId,
  frameSizeId,
  frameStyleColourId,
  glassTypeId,
  matStyleId,
  borderMm,
  borderDirection,
}) => {
  const params = {
    width,
    height,
    unit,
    image_key: imageKey,
    paper_type_id: paperTypeId,
    frame_size_id: frameSizeId,
  };

  if (frameStyleColourId) params.fsc_id = frameStyleColourId;
  if (glassTypeId) params.glass_type_id = glassTypeId;
  if (matStyleId) params.mat_style_id = matStyleId;
  if (borderMm > 0) {
    params.border_mm = borderMm;
    params.border_direction = borderDirection || "in";
  }

  return `${frameDesignerPath}?${new URLSearchParams(params).toString()}`;
};

const buildOriginalSizeDimensions = (perimeterMm, sourceRatio) => {
  const longSideRatio = sourceRatio > 1 ? sourceRatio : 1 / sourceRatio;
  const shortMm = Math.floor(perimeterMm / (2 * (longSideRatio + 1)));
  const longMm = Math.floor(shortMm * longSideRatio);

  return { longMm, shortMm };
};

const buildOriginalSizeTitle = ({ longMm, shortMm, sourceRatio }) =>
  sourceRatio > 1 ? `${longMm} x ${shortMm}mm` : `${shortMm} x ${longMm}mm`;

const PrintSizeForm = (props) => {
  const initialCustomSize = getCustomSizeFromURL(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const smartEnhanceDefault = props.is_pro ? false : true;
  const initialEffects = {
    filter: props.image?.effects?.filter || null,
    viesus_correct: props.image?.effects?.viesus_correct ?? smartEnhanceDefault,
  };
  // CROPPER NaN FIX: Helper functions to prevent NaN values in crop coordinates
  // This addresses the issue where react-easy-crop sometimes returns NaN values
  // during complex state transitions, aspect ratio changes, or initialization race conditions

  // Helper function to get a safe default crop object
  const getDefaultCrop = () => ({ x: 0, y: 0 });

  // Thumbs SVG icon
  const ThumbsIcon = ({ className = "", flipped = false, rotate = false }) => {
    const transforms = [];
    if (flipped) transforms.push("scale-y-[-1]");
    if (rotate) transforms.push(`rotate-90`);

    return (
      <svg
        className={`w-5 h-5 ${transforms.length > 0 ? `transform ${transforms.join(" ")}` : ""} ${className}`}
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.94531 3.40137C7.94531 2.95429 8.12552 2.5228 8.4502 2.20313C8.67355 1.98332 8.95338 1.83017 9.25684 1.75488L12.2354 8.35059L12.2354 16.3008L3.75781 16.3008C3.53152 16.3032 3.31328 16.225 3.14356 16.083C2.97413 15.9412 2.86464 15.7458 2.83203 15.5342L2.83203 15.5332L1.70996 8.33301C1.69057 8.2061 1.69991 8.07666 1.73633 7.95313C1.77291 7.82911 1.83654 7.71305 1.92383 7.61426C2.01112 7.51555 2.12003 7.43622 2.24316 7.38184C2.36642 7.32741 2.50082 7.29927 2.63672 7.30078L7.24512 7.30078C7.63172 7.30078 7.94531 6.98719 7.94531 6.60059L7.94531 3.40137ZM16.2998 15.4014C16.2997 15.6361 16.2053 15.8637 16.0332 16.0332C15.8604 16.2033 15.6231 16.3008 15.374 16.3008L13.6357 16.3008L13.6357 8.90137L15.374 8.90137C15.6231 8.90137 15.8604 8.9989 16.0332 9.16895C16.2053 9.33854 16.2998 9.56598 16.2998 9.80078L16.2998 15.4014ZM6.54492 5.90137L2.64453 5.90137C2.31182 5.89872 1.9821 5.96623 1.67773 6.10059C1.37094 6.2361 1.096 6.43629 0.874024 6.6875C0.652151 6.93867 0.488371 7.23533 0.393555 7.55664C0.298712 7.87814 0.275082 8.21659 0.326173 8.54785L0.327149 8.54883L1.44824 15.749C1.53348 16.2997 1.81761 16.7996 2.24512 17.1572C2.67073 17.5133 3.21076 17.7044 3.76563 17.7002L3.76563 17.7012L15.374 17.7012C15.9872 17.7012 16.5777 17.4612 17.0146 17.0313C17.4519 16.6009 17.7001 16.0148 17.7002 15.4014L17.7002 9.80078C17.7002 9.18721 17.452 8.6013 17.0146 8.1709C16.5776 7.74083 15.9873 7.50098 15.374 7.50098L13.3867 7.50098L10.3223 0.712891C10.209 0.462158 9.95871 0.300782 9.68359 0.300782C8.8549 0.300864 8.05812 0.625116 7.46875 1.20508C6.87892 1.78554 6.54492 2.57555 6.54492 3.40137L6.54492 5.90137Z"
          fill="currentColor"
        />
      </svg>
    );
  };

  const [state, setState] = useState({
    cropOrientationPortrait: null,
    loading: false,
    zoom: 1,
    lowDPI: null,
    dpiState: null, // New state to track DPI level: 'great', 'ok', 'too-low'
    cropperReady: false,
    saving: false,
    tabURLS: {
      0: props.print_size_path,
      1: props.square_print_size_path,
      2: props.original_print_sizes_path,
    },
    tabIndex: 0,
    sizes: null,
    print_size: null,
    print_size_costs: null,
    paper_type_id: props.paper_type_id,
    print_price: null,
    ratio: null,
    crop: getDefaultCrop(),
    croppedArea: null,
    croppedAreaPixels: null,
    preview_width: null,
    preview_height: null,
    dpi: null,
    pendingRatio: null,
    cropModalOpen: false, // New state for crop editing modal
  });
  const [customSize, setCustomSize] = useState({
    width: validUrlDimension(props.initial_custom_width_mm)
      ? props.initial_custom_width_mm
      : initialCustomSize.width,
    height: validUrlDimension(props.initial_custom_height_mm)
      ? props.initial_custom_height_mm
      : initialCustomSize.height,
    unit: CUSTOM_SIZE_DEFAULT_UNIT.title,
    matchError: "",
  });
  const [effectsAccordionOpen, setEffectsAccordionOpen] = useState(false);
  const [committedEffects, setCommittedEffects] = useState(initialEffects);
  const [draftEffects, setDraftEffects] = useState(initialEffects);
  const [effectsImageLoaded, setEffectsImageLoaded] = useState(false);
  const [, setEffectsLoadedImages] = useState(new Set());
  const [cropperImageLoading, setCropperImageLoading] = useState(true);
  const [isCropInteracting, setIsCropInteracting] = useState(false);
  const cropInteractionTimer = useRef(null);
  const prevCropperUrlRef = useRef(null);
  const customTabAllowed = customPrintTabEnabled({
    isPro: props.is_pro,
    forceCustomTab: props.force_custom_tab,
  });
  const isCanvasMedia = props.media_type === "canvas";
  const customMaxLongMm = useMemo(() => {
    // Canvas is a stretched/floated product, so it is not constrained by the
    // glass or standard print limits — only the larger float artwork limit.
    const candidates = isCanvasMedia
      ? [props.max_float_artwork_long_mm]
      : [props.max_glass_long_mm, props.max_artwork_long_mm];

    const validCandidates = candidates
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0);

    return validCandidates.length
      ? Math.min(...validCandidates)
      : CUSTOM_SIZE_MAX_MM;
  }, [
    isCanvasMedia,
    props.max_float_artwork_long_mm,
    props.max_glass_long_mm,
    props.max_artwork_long_mm,
  ]);
  const customMaxShortMm = useMemo(() => {
    if (!isCanvasMedia) {
      return null;
    }

    const floatShort = Number(props.max_float_artwork_short_mm);
    return Number.isFinite(floatShort) && floatShort > 0 ? floatShort : null;
  }, [isCanvasMedia, props.max_float_artwork_short_mm]);
  const customMaxCombinedMm = useMemo(() => {
    if (!isCanvasMedia) {
      return null;
    }

    return resolveCombinedLimitMm(
      props.max_float_artwork_combined_mm,
      customMaxLongMm,
      customMaxShortMm,
    );
  }, [
    isCanvasMedia,
    props.max_float_artwork_combined_mm,
    customMaxLongMm,
    customMaxShortMm,
  ]);
  const customSizeValidation = useMemo(
    () =>
      getCustomSizeValidationState({
        width: customSize.width,
        height: customSize.height,
        unit: customSize.unit,
        maxMm: customMaxLongMm,
        maxShortMm: customMaxShortMm,
        maxCombinedMm: customMaxCombinedMm,
      }),
    [
      customSize.height,
      customSize.unit,
      customSize.width,
      customMaxLongMm,
      customMaxShortMm,
      customMaxCombinedMm,
    ],
  );
  const isCustomTab =
    customTabAllowed && state.tabIndex === PRINT_TAB_INDEXES.custom;
  const activeLongestInches = isCustomTab
    ? customSizeValidation.longestInches
    : state.print_size?.longest_inches;
  const hasResolutionContext = !!(
    state.croppedAreaPixels &&
    state.preview_width &&
    activeLongestInches
  );
  const canOfferUpscale = upscaleEligible(props.image, props.upscale_available);
  const upscaleCandidate = canOfferUpscale && props.media_type !== "print_only";
  const [upscaleGroup, setUpscaleGroup] = useState(null);
  const showUpscaleOffer =
    upscaleCandidate &&
    upscaleTreatmentActive(props.upscale_test_key, upscaleGroup);
  const upscale = {
    scale: props.upscale_scale,
    shortestPrintLength: props.upscale_shortest_print_length,
  };
  const [upscaleModal, setUpscaleModal] = useState({
    open: false,
    source: null,
  });
  useEffect(() => {
    resolveUpscaleTestGroup({
      testKey: props.upscale_test_key,
      onGroup: setUpscaleGroup,
    });
  }, [props.upscale_test_key]);
  const openUpscaleModal = (source) => setUpscaleModal({ open: true, source });
  const upscaleContext = () =>
    upscaleEventContext(props.image, props.upscale_available);

  // Helper function to get DPI state
  const getDPIState = (dpi) => {
    if (dpi <= 90) {
      return "too-low";
    } else if (dpi > 90 && dpi <= 125) {
      return "ok";
    } else {
      return "great";
    }
  };
  const hasTrackedZoomRef = useRef(false);
  const tabCacheRef = useRef(new Map());
  const tabRequestRef = useRef(0);
  const onTabChangeRef = useRef(null);

  // Helper function to update URL with tab parameter
  const updateURLWithTab = (tabIndex, historyMode) => {
    if (!historyMode) return;

    const tabName = getTabNameFromIndex(tabIndex);
    const url = new URL(window.location);
    url.searchParams.set("tab", tabName);

    if (tabIndex !== PRINT_TAB_INDEXES.custom) {
      url.searchParams.delete("width_mm");
      url.searchParams.delete("height_mm");
    }

    if (url.href === window.location.href) return;
    window.history[historyMode === "replace" ? "replaceState" : "pushState"](
      window.history.state,
      "",
      url,
    );
  };

  const updateURLWithCustomSize = useCallback(() => {
    if (!isCustomTab) {
      return;
    }

    const url = new URL(window.location);
    url.searchParams.set("tab", "custom");

    if (
      customSize.unit === CUSTOM_SIZE_DEFAULT_UNIT.title &&
      validUrlDimension(customSize.width)
    ) {
      url.searchParams.set("width_mm", customSize.width);
    } else {
      url.searchParams.delete("width_mm");
    }

    if (
      customSize.unit === CUSTOM_SIZE_DEFAULT_UNIT.title &&
      validUrlDimension(customSize.height)
    ) {
      url.searchParams.set("height_mm", customSize.height);
    } else {
      url.searchParams.delete("height_mm");
    }

    window.history.replaceState(window.history.state, "", url);
  }, [customSize.height, customSize.unit, customSize.width, isCustomTab]);

  useEffect(() => {
    const pathname = window.location.pathname;
    const tabFromLocation = () =>
      getTabIndexFromURL(window.location.search, {
        customTabAllowed,
        sourceRatio:
          props.source_ratio ||
          (props.image?.height ? props.image.width / props.image.height : null),
      });

    // Normalize the initial URL without inserting a second prints entry.
    onTabChangeRef.current(tabFromLocation(), { historyMode: "replace" });

    const restoreLocation = () => {
      const url = new URL(window.location.href);
      if (
        url.pathname !== pathname ||
        url.searchParams.get("image_key") !== props.image.key
      ) {
        return;
      }

      const tabIndex = tabFromLocation();
      if (tabIndex === PRINT_TAB_INDEXES.custom) {
        setCustomSize((previous) => ({
          ...previous,
          ...getCustomSizeFromURL(url.search),
          unit: CUSTOM_SIZE_DEFAULT_UNIT.title,
          matchError: "",
        }));
      }
      onTabChangeRef.current(tabIndex, { historyMode: null });
    };

    window.addEventListener("popstate", restoreLocation);
    return () => window.removeEventListener("popstate", restoreLocation);
  }, []);

  useEffect(() => {
    updateURLWithCustomSize();
  }, [updateURLWithCustomSize]);

  // Add effect to trigger DPI update when croppedAreaPixels changes
  useEffect(() => {
    if (
      !state.croppedAreaPixels ||
      !state.preview_width ||
      !activeLongestInches
    ) {
      return;
    }

    const { croppedAreaPixels, preview_width, preview_height } = state;
    const { image } = props;

    if (!image || !image.width || preview_width <= 0) {
      return;
    }

    // react-easy-crop returns croppedAreaPixels relative to the displayed image size
    // We need to scale it to the auto-oriented image dimensions for DPI calculation
    const displaySize = displayImageSize({
      imageWidth: image.width,
      imageHeight: image.height,
      previewWidth: preview_width,
      previewHeight: preview_height,
    });
    const scale = displaySize.width / preview_width;

    if (!isFinite(scale) || scale <= 0) {
      return;
    }

    const actualCropWidth = croppedAreaPixels.width * scale;
    const actualCropHeight = croppedAreaPixels.height * scale;

    if (
      !isFinite(actualCropWidth) ||
      !isFinite(actualCropHeight) ||
      actualCropWidth <= 0 ||
      actualCropHeight <= 0
    ) {
      return;
    }

    const longest = Math.max(actualCropWidth, actualCropHeight);
    const dpi = Math.round(longest / activeLongestInches);

    if (!isFinite(dpi) || dpi <= 0) {
      return;
    }

    updateState({
      dpi,
      lowDPI: dpi <= 90,
      dpiState: getDPIState(dpi),
    });
    if (getDPIState(dpi) === "too-low") trackResolutionBlocked(dpi);
  }, [
    activeLongestInches,
    props.image,
    state.croppedAreaPixels,
    state.preview_width,
    state.preview_height,
  ]);

  const activeSizeProps = () =>
    isCustomTab
      ? {
          print_size_title: "Custom size",
          is_custom_size: true,
          width: customSizeValidation.width,
          height: customSizeValidation.height,
          unit: customSizeValidation.unitTitle,
        }
      : {
          print_size_title: state.print_size?.title,
          frame_size_id: state.print_size?.id,
          is_custom_size: false,
        };
  const activeSizeKey = isCustomTab
    ? customSizeValidation.valid &&
      `custom:${customSizeValidation.width}x${customSizeValidation.height}${customSizeValidation.unitTitle}`
    : state.print_size?.id;
  const upscaleOfferVisible =
    showUpscaleOffer &&
    hasResolutionContext &&
    !!state.dpiState &&
    !effectsAccordionOpen &&
    !state.loading;
  const trackedOfferRef = useRef(null);
  const trackedBlockedRef = useRef(new Set());

  useEffect(() => {
    if (!upscaleOfferVisible || trackedOfferRef.current === props.image?.id) {
      return;
    }
    trackedOfferRef.current = props.image?.id;
    trackEvent("Upscale: Offer Shown", {
      ...upscaleContext(),
      ...activeSizeProps(),
      dpi: state.dpi,
      dpi_state: state.dpiState,
      longest_mm: inchesToMm(activeLongestInches),
      placement: "size_picker",
      largest_available_size_title: largestAvailableASize(
        props.shortest_print_length,
      ),
      unlockable_size_count: isCustomTab
        ? 0
        : upscaleUnlockCopy(state.sizes).count,
    });
  }, [upscaleOfferVisible, props.image?.id]);

  function trackResolutionBlocked(dpi) {
    if (!activeSizeKey || trackedBlockedRef.current.has(activeSizeKey)) return;
    trackedBlockedRef.current.add(activeSizeKey);
    trackEvent("Print Size: Resolution Blocked", {
      ...upscaleContext(),
      ...activeSizeProps(),
      dpi,
      longest_mm: inchesToMm(activeLongestInches),
      max_longest_mm_ok: maxLongestMmOk(props.image),
    });
  }

  useEffect(() => {
    if (
      !isCustomTab ||
      !customSizeValidation.valid ||
      !customSizeValidation.ratio
    ) {
      return;
    }

    setState((prevState) => {
      const nextState = buildRatioSelectionState({
        cropperReady: prevState.cropperReady,
        ratio: customSizeValidation.ratio,
        updates: {},
      });

      if (
        prevState.ratio === nextState.ratio &&
        prevState.pendingRatio === nextState.pendingRatio
      ) {
        return prevState;
      }

      return {
        ...prevState,
        ...nextState,
      };
    });
  }, [
    customSizeValidation.ratio,
    customSizeValidation.valid,
    isCustomTab,
    state.cropperReady,
  ]);

  useEffect(() => {
    if (!effectsAccordionOpen) {
      return;
    }

    setEffectsLoadedImages((prev) => {
      const next = new Set();
      const hasEffects = draftEffects.filter || draftEffects.viesus_correct;

      if (prev.has("original")) {
        next.add("original");
      }

      setEffectsImageLoaded(!hasEffects && next.has("original"));
      return next;
    });
  }, [draftEffects.filter, draftEffects.viesus_correct, effectsAccordionOpen]);

  const updateState = (newState) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const buildEffectsUrl = useCallback(
    (effectsConfig, cropTransform = null) => {
      if (!props.image?.original_url) {
        return props.preview_url;
      }

      const cl = new cloudinary.Cloudinary({
        cloud_name: "framefox",
        secure: true,
      });
      const transformations = [{ width: 1000, crop: "fit" }];

      if (cropTransform) {
        transformations.push(cropTransform);
      }

      if (effectsConfig?.filter) {
        transformations.push({ effect: `art:${effectsConfig.filter}` });
      }

      if (effectsConfig?.viesus_correct) {
        transformations.push({ effect: "viesus_correct" });
      }

      transformations.push({ width: 1000, crop: "fit" });

      return cl.url(props.image.original_url, {
        type: props.image.resource_type || "upload",
        transformation: transformations,
        format: "jpg",
      });
    },
    [props.image?.original_url, props.image?.resource_type, props.preview_url],
  );

  const currentCropTransform = useCallback(() => {
    if (
      state.croppedAreaPixels &&
      state.preview_width &&
      state.preview_width > 0
    ) {
      const scale = 1000 / state.preview_width;

      return {
        width: Math.max(1, Math.round(state.croppedAreaPixels.width * scale)),
        height: Math.max(1, Math.round(state.croppedAreaPixels.height * scale)),
        x: Math.max(0, Math.round(state.croppedAreaPixels.x * scale)),
        y: Math.max(0, Math.round(state.croppedAreaPixels.y * scale)),
        crop: "crop",
      };
    }

    if (!props.image?.crop || !props.image?.width) {
      return null;
    }

    const coordinateWidth = cropCoordinateSpaceWidth(props.image);
    if (!coordinateWidth) {
      return null;
    }

    return {
      width: parseInt((props.image.crop.cw * 1000) / coordinateWidth),
      height: parseInt((props.image.crop.ch * 1000) / coordinateWidth),
      x: parseInt((props.image.crop.cx * 1000) / coordinateWidth),
      y: parseInt((props.image.crop.cy * 1000) / coordinateWidth),
      crop: "crop",
    };
  }, [
    props.image?.crop,
    props.image?.crop_space_width,
    props.image?.width,
    state.croppedAreaPixels,
    state.preview_width,
  ]);

  const handleEffectsImageLoaded = useCallback(
    (imageKey) => {
      setEffectsLoadedImages((prev) => {
        const next = new Set(prev);
        next.add(imageKey);

        const expectedImages =
          draftEffects.filter || draftEffects.viesus_correct ? 2 : 1;

        if (next.size >= expectedImages) {
          setEffectsImageLoaded(true);
        }

        return next;
      });
    },
    [draftEffects.filter, draftEffects.viesus_correct],
  );

  const openEffectsAccordion = () => {
    setDraftEffects(committedEffects);
    setEffectsAccordionOpen(true);
    if (isMobile) {
      setTimeout(() => window.scrollTo({ top: 120, behavior: "smooth" }), 50);
    }
  };

  const cancelEffectsEditing = () => {
    setDraftEffects(committedEffects);
    setEffectsAccordionOpen(false);
  };

  const doneEffectsEditing = () => {
    setCommittedEffects(draftEffects);
    setEffectsAccordionOpen(false);
    setCropperImageLoading(true);
  };

  // Helper function to validate and sanitize crop coordinates
  const sanitizeCrop = (crop) => {
    if (!crop || typeof crop !== "object") {
      return getDefaultCrop();
    }

    return {
      x: isNaN(crop.x) ? 0 : crop.x,
      y: isNaN(crop.y) ? 0 : crop.y,
    };
  };

  // Helper function to validate and sanitize croppedAreaPixels
  const sanitizeCroppedAreaPixels = (croppedAreaPixels) => {
    if (!croppedAreaPixels || typeof croppedAreaPixels !== "object") {
      return null;
    }

    const hasNaN =
      isNaN(croppedAreaPixels.x) ||
      isNaN(croppedAreaPixels.y) ||
      isNaN(croppedAreaPixels.width) ||
      isNaN(croppedAreaPixels.height);

    if (hasNaN) {
      return null;
    }

    return croppedAreaPixels;
  };

  const sanitizeCroppedArea = (croppedArea) => {
    if (!croppedArea || typeof croppedArea !== "object") {
      return null;
    }

    const hasInvalidValue =
      !Number.isFinite(croppedArea.x) ||
      !Number.isFinite(croppedArea.y) ||
      !Number.isFinite(croppedArea.width) ||
      !Number.isFinite(croppedArea.height);

    if (hasInvalidValue) {
      return null;
    }

    return croppedArea;
  };

  const cropGestureRef = useRef(false);
  const cropAdjustedRef = useRef(false);
  const cropInputsRef = useRef(new Set());
  const cropModalOpenedRef = useRef(false);

  const trackCropAdjusted = useCallback(
    (input, location) => {
      if (cropInputsRef.current.has(input)) return;
      const first = !cropAdjustedRef.current;
      cropAdjustedRef.current = true;
      cropInputsRef.current.add(input);
      if (!first) return;
      trackEvent("Print Size: Crop Adjusted", {
        image_id: props.image?.id,
        input,
        location,
        is_mobile: isMobile,
      });
    },
    [props.image?.id],
  );
  const onCropInteractionStart = useCallback(() => {
    cropGestureRef.current = true;
  }, []);
  const onCropInteractionEnd = useCallback(() => {
    cropGestureRef.current = false;
  }, []);

  const markCropInteracting = useCallback(() => {
    setIsCropInteracting(true);
    clearTimeout(cropInteractionTimer.current);
    cropInteractionTimer.current = setTimeout(() => {
      setIsCropInteracting(false);
    }, 400);
  }, []);

  const onCropChange = useCallback(
    (crop) => {
      const sanitizedCrop = sanitizeCrop(crop);
      updateState({ crop: sanitizedCrop });
      markCropInteracting();
      if (cropGestureRef.current) trackCropAdjusted("drag", "page");
    },
    [markCropInteracting, trackCropAdjusted],
  );

  const onZoomChange = useCallback(
    (zoom) => {
      const sanitizedZoom = isNaN(zoom) ? 1 : Math.max(1, Math.min(3, zoom));
      updateState({ zoom: sanitizedZoom });
      markCropInteracting();
      if (cropGestureRef.current) trackCropAdjusted("pinch", "page");
    },
    [markCropInteracting, trackCropAdjusted],
  );

  // Callback for when crop is completed
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    const sanitizedCroppedArea = sanitizeCroppedArea(croppedArea);
    const sanitizedCroppedAreaPixels =
      sanitizeCroppedAreaPixels(croppedAreaPixels);
    if (sanitizedCroppedArea || sanitizedCroppedAreaPixels) {
      updateState({
        ...(sanitizedCroppedArea ? { croppedArea: sanitizedCroppedArea } : {}),
        ...(sanitizedCroppedAreaPixels
          ? { croppedAreaPixels: sanitizedCroppedAreaPixels }
          : {}),
      });
    }
  }, []);

  // Callback for when cropper is ready
  const onMediaLoaded = useCallback(
    (mediaSize) => {
      const resolvedPreviewSize = resolvePreviewSize({
        previewWidth: mediaSize.naturalWidth || mediaSize.width,
        previewHeight: mediaSize.naturalHeight || mediaSize.height,
        imageWidth: props.image?.width,
        imageHeight: props.image?.height,
      });

      const width = resolvedPreviewSize.previewWidth;
      const height = resolvedPreviewSize.previewHeight;
      const megapixels = Math.round(((width * height) / 1000000) * 10) / 10;
      const orientation =
        width > height ? "landscape" : height > width ? "portrait" : "square";

      const upscaleProps = upscaleEventContext(
        props.image,
        props.upscale_available,
      );
      if (upscaleProps.upscale_offer_available) {
        trackUpscaleExposure({
          imageId: props.image?.id,
          testKey: props.upscale_test_key,
          onGroup: setUpscaleGroup,
        });
      }
      trackEvent("Print Size: Image Detail", {
        width: width,
        height: height,
        mpx: megapixels,
        orientation: orientation,
        ...upscaleProps,
        source_width: props.image?.width,
        source_height: props.image?.height,
        max_longest_mm_ok: maxLongestMmOk(props.image),
        largest_available_size_title: largestAvailableASize(
          props.shortest_print_length,
        ),
      });

      setState((prevState) => ({
        ...prevState,
        cropperReady: true,
        preview_width: resolvedPreviewSize.previewWidth,
        preview_height: resolvedPreviewSize.previewHeight,
        ...(prevState.pendingRatio
          ? {
              ratio: prevState.pendingRatio,
              pendingRatio: null,
            }
          : {}),
      }));
      setCropperImageLoading(false);
    },
    [props.image?.height, props.image?.width],
  );

  const saveSizes = (data) => {
    const { shortest_print_length } = props;

    // Mark sizes as disabled instead of filtering them out
    let sizes = data.sizes.map((item) => {
      const disabled = item.shortest_inches > shortest_print_length;
      return {
        ...item,
        disabled,
        unlockable:
          upscaleCandidate &&
          disabled &&
          fitsAfterUpscale({ upscale, shortInches: item.shortest_inches }),
      };
    });
    setSizes(sizes);
  };

  const saveOriginalSizes = (data) => {
    const {
      source_ratio,
      shortest_print_length,
      image,
      max_glass_short_mm,
      max_glass_long_mm,
    } = props;
    const dpi = 100;
    const longEdgePX = Math.max(image.width, image.height);

    // Process all sizes instead of filtering
    let sizes = data.sizes.map((item) => {
      const { longMm, shortMm } = buildOriginalSizeDimensions(
        item.perimeter_mm,
        source_ratio,
      );
      const adjustedLongInches = longMm / 25.4;
      const adjustedShortInches = shortMm / 25.4;
      const exceedsGlassLimits =
        longMm > max_glass_long_mm || shortMm > max_glass_short_mm;
      const exceedsPhysicalLimits =
        longMm > props.max_artwork_long_mm || exceedsGlassLimits;
      const exceedsResolution =
        adjustedLongInches > longEdgePX / dpi ||
        adjustedShortInches > shortest_print_length;

      return {
        ...item,
        short: shortMm,
        long: longMm,
        long_mm: longMm,
        short_mm: shortMm,
        unit: "mm",
        title: buildOriginalSizeTitle({
          longMm,
          shortMm,
          sourceRatio: source_ratio,
        }),
        longest_inches: adjustedLongInches,
        disabled: exceedsResolution || exceedsPhysicalLimits,
        unlockable:
          upscaleCandidate &&
          exceedsResolution &&
          !exceedsPhysicalLimits &&
          fitsAfterUpscale({
            upscale,
            shortInches: adjustedShortInches,
            longInches: adjustedLongInches,
            longEdgePx: longEdgePX,
            dpi,
          }),
      };
    });

    setSizes(sizes);
  };

  const setSizes = (sizes) => {
    const { source_ratio, country_name } = props;
    const activePaperTypeId = state.paper_type_id || props.paper_type_id;

    // Only prioritize standard (ISO) sizes for NZ
    const isNZ =
      country_name && country_name.toLowerCase().includes("new zealand");
    const enabledSizes = sizes.filter((size) => !size.disabled);
    const enabledIsoSizes = enabledSizes.filter((size) => size.is_a_size);
    const enabledInchSizes = enabledSizes.filter((size) => !size.is_a_size);

    let selectedSize;

    // For NZ: prioritize ISO sizes. For AU: use all enabled sizes
    const sizesToConsider =
      isNZ && enabledIsoSizes.length > 0 ? enabledIsoSizes : enabledSizes;

    // Filter enabled sizes with frame_price_no_mat under 100
    const affordableSizes = sizesToConsider.filter(
      (size) => size.frame_price_no_mat < 100,
    );

    if (affordableSizes.length > 0) {
      // Find the one with the highest frame_price_no_mat
      selectedSize = affordableSizes.reduce((highest, current) =>
        current.frame_price_no_mat > highest.frame_price_no_mat
          ? current
          : highest,
      );
    } else {
      // Fall back to original logic if no affordable sizes
      selectedSize =
        sizesToConsider.length > 0
          ? sizesToConsider[Math.floor(sizesToConsider.length / 2)]
          : sizes[Math.floor(sizes.length / 2)]; // fallback to original logic if all disabled
    }

    // Calculate aspect ratio based on source ratio
    let ratio =
      source_ratio > 1
        ? selectedSize.long / selectedSize.short
        : selectedSize.short / selectedSize.long;

    // Common state updates
    const selectedPaperPrice =
      selectedSize.prices.find(
        (item) => item.paper_type_id === activePaperTypeId,
      ) || selectedSize.prices[0];

    const newState = {
      sizes,
      print_size: selectedSize,
      print_size_costs: selectedSize.prices,
      paper_type_id: selectedPaperPrice.paper_type_id,
      print_price: selectedPaperPrice.price,
      loading: false,
      cropOrientationPortrait: source_ratio > 1,
    };

    updateState(
      buildRatioSelectionState({
        cropperReady: state.cropperReady,
        ratio,
        updates: newState,
      }),
    );
  };

  const sizeSelected = (active) => {
    // Don't allow selection of disabled sizes
    if (active.disabled) {
      return;
    }

    const { cropOrientationPortrait, paper_type_id } = state;
    const { source_ratio } = props;

    let print_cost = active.prices.find(
      (item) => item.paper_type_id === paper_type_id,
    );
    let new_price = print_cost ? print_cost.price : active.prices[0].price;
    let new_paper_type = print_cost
      ? print_cost.paper_type_id
      : paper_type_id || active.prices[0].paper_type_id;

    // Fix orientation logic - cropOrientationPortrait should take precedence when set
    // If cropOrientationPortrait is null (initial state), fall back to source_ratio
    let isPortrait =
      cropOrientationPortrait !== null
        ? cropOrientationPortrait
        : source_ratio > 1;

    let ratio = isPortrait
      ? active.long / active.short
      : active.short / active.long;

    updateState(
      buildRatioSelectionState({
        cropperReady: state.cropperReady,
        ratio,
        updates: {
          print_size: active,
          print_size_costs: active.prices,
          print_price: new_price,
          paper_type_id: new_paper_type,
        },
      }),
    );
  };

  const saveCropSelection = ({ ratio }) =>
    axios({
      method: "PATCH",
      url: props.crop_image_path,
      data: {
        image: {
          id: props.image.id,
          cx: state.croppedAreaPixels.x,
          cy: state.croppedAreaPixels.y,
          cw: state.croppedAreaPixels.width,
          ch: state.croppedAreaPixels.height,
          viesus_correct: committedEffects.viesus_correct,
          filter: committedEffects.filter,
        },
        preview_width: state.preview_width,
        preview_height: state.preview_height,
        frame_style_colour_id: props.frame_style_colour_id,
        expected_ratio: ratio,
      },
    });

  const finalisedSizeProps = ({ long, short, longestInches }) => {
    const longestMm = Number(longestInches) * 25.4;
    const shortestMm = long > 0 ? (longestMm * short) / long : null;
    return {
      ...upscaleContext(),
      dpi: state.dpi,
      dpi_state: state.dpiState,
      longest_mm: inchesToMm(longestInches),
      area_cm2:
        longestMm && shortestMm
          ? Math.round((longestMm * shortestMm) / 100)
          : null,
      crop_adjusted: cropAdjustedRef.current,
      crop_inputs: [...cropInputsRef.current],
      crop_modal_opened: cropModalOpenedRef.current,
      zoom: Math.round(state.zoom * 100) / 100,
      crop_offset_pct: cropOffsetPct(state.croppedArea),
    };
  };

  const completeStandardResponse = (response) => {
    const { print_size, paper_type_id, cropOrientationPortrait } = state;
    const {
      frame_designer_path,
      frame_style_colour_id,
      glass_type_id,
      mat_style_id,
      source_ratio,
    } = props;

    // Fix orientation logic - cropOrientationPortrait should take precedence when set
    // If cropOrientationPortrait is null (initial state), fall back to source_ratio
    let isPortrait =
      cropOrientationPortrait !== null
        ? cropOrientationPortrait
        : source_ratio > 1;

    let width = isPortrait ? print_size.long : print_size.short;
    let height = isPortrait ? print_size.short : print_size.long;

    trackEvent("Print Size: Finalised", {
      long: print_size.long,
      short: print_size.short,
      unit: print_size.unit,
      paper_type_id: paper_type_id,
      frame_size_id: print_size.id,
      print_size_title: print_size.title,
      is_custom_size: false,
      ...finalisedSizeProps({
        long: print_size.long,
        short: print_size.short,
        longestInches: print_size.longest_inches,
      }),
    });

    window.location.href = buildDesignerRedirectUrl({
      frameDesignerPath: frame_designer_path,
      width,
      height,
      unit: print_size.unit,
      imageKey: response.data.image.key,
      paperTypeId: paper_type_id,
      frameSizeId: print_size.id,
      frameStyleColourId: frame_style_colour_id,
      glassTypeId: glass_type_id,
      matStyleId: mat_style_id,
      borderMm: props.border_mm,
      borderDirection: props.border_direction,
    });
  };

  const completeCustomResponse = (response, matchedFrameSizeId) => {
    const width = customSizeValidation.width;
    const height = customSizeValidation.height;
    const unit = customSizeValidation.unitTitle;

    trackEvent("Print Size: Finalised", {
      long: Math.max(width, height),
      short: Math.min(width, height),
      unit,
      paper_type_id: state.paper_type_id,
      frame_size_id: matchedFrameSizeId,
      print_size_title: "Custom size",
      is_custom_size: true,
      requested_width: width,
      requested_height: height,
      requested_unit: unit,
      ...finalisedSizeProps({
        long: Math.max(width, height),
        short: Math.min(width, height),
        longestInches: customSizeValidation.longestInches,
      }),
    });

    window.location.href = buildDesignerRedirectUrl({
      frameDesignerPath: props.frame_designer_path,
      width,
      height,
      unit,
      imageKey: response.data.image.key,
      paperTypeId: state.paper_type_id,
      frameSizeId: matchedFrameSizeId,
      frameStyleColourId: props.frame_style_colour_id,
      glassTypeId: props.glass_type_id,
      matStyleId: props.mat_style_id,
      borderMm: props.border_mm,
      borderDirection: props.border_direction,
    });
  };

  const handleError = (error) => {
    // Reset saving state
    updateState({ saving: false });

    // Show error message from backend validation
    const errorMessage =
      error.response?.data?.error ||
      "An error occurred while saving. Please try again.";

    alert(errorMessage);
  };

  const handleCustomSizeChange = (field, value) => {
    setCustomSize((prevState) => ({
      ...prevState,
      [field]: value,
      matchError: "",
    }));
  };

  const flipCustomSize = () => {
    const flipped = flipCustomSizeDimensions({
      width: customSize.width,
      height: customSize.height,
    });

    setCustomSize((prevState) => ({
      ...prevState,
      ...flipped,
      matchError: "",
    }));
  };

  const validateAndFrame = async (e) => {
    e.preventDefault();

    updateState({ saving: true });

    if (isCustomTab && !customSizeValidation.valid) {
      updateState({ saving: false });
      setCustomSize((prevState) => ({
        ...prevState,
        matchError: customSizeValidation.message,
      }));
      return;
    }

    const ratio = isCustomTab ? customSizeValidation.ratio : state.ratio;
    const validationResult = getCropValidationResult({
      croppedArea: state.croppedArea,
      croppedAreaPixels: state.croppedAreaPixels,
      ratio,
      printSize: state.print_size,
      requirePrintSize: !isCustomTab,
      previewWidth: state.preview_width,
      previewHeight: state.preview_height,
      imageWidth: props.image?.width,
      imageHeight: props.image?.height,
    });

    if (validationResult.errorMessage) {
      if (validationResult.code === "ratio_mismatch") {
        const activePrintSize = isCustomTab
          ? {
              title: "Custom size",
              width: customSizeValidation.width,
              height: customSizeValidation.height,
              unit: customSizeValidation.unitTitle,
            }
          : state.print_size;

        const errorProps = buildCropRatioErrorProps(props, {
          component: "PrintSizeForm",
          currentCropRatio: validationResult.currentCropRatio,
          expectedRatio: ratio,
          croppedArea: state.croppedArea,
          croppedAreaPixels: state.croppedAreaPixels,
          crop: state.crop,
          previewWidth: state.preview_width,
          previewHeight: state.preview_height,
          zoom: state.zoom,
          dpi: state.dpi,
          lowDPI: state.lowDPI,
          cropOrientationPortrait: state.cropOrientationPortrait,
          currentCropRatioSource: validationResult.ratioSource,
          printSize: activePrintSize,
          isMobile,
          isCustomSize: isCustomTab,
          customSize: isCustomTab
            ? {
                width: customSizeValidation.width,
                height: customSizeValidation.height,
                unit: customSizeValidation.unitTitle,
              }
            : null,
        });

        trackEvent("Crop Ratio Error", errorProps);
        captureSentryMessage("Crop Ratio Error", errorProps);
      }

      updateState({ saving: false });
      alert(validationResult.errorMessage);
      return;
    }

    if (isCustomTab) {
      try {
        const matchResponse = await axios({
          method: "GET",
          url: props.custom_size_match_path,
          params: {
            width: customSizeValidation.width,
            height: customSizeValidation.height,
            unit: customSizeValidation.unitTitle,
            paper_type_id: state.paper_type_id,
          },
        });

        const matchedFrameSizeId = matchResponse.data.frame_sku_size?.id;

        if (!matchedFrameSizeId) {
          throw new Error("No matching frame size found");
        }

        const cropResponse = await saveCropSelection({ ratio });
        completeCustomResponse(cropResponse, matchedFrameSizeId);
      } catch (error) {
        updateState({ saving: false });
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "No frame available for this size. Please try another.";

        setCustomSize((prevState) => ({
          ...prevState,
          matchError: errorMessage,
        }));
      }

      return;
    }

    saveCropSelection({ ratio })
      .then(completeStandardResponse)
      .catch(handleError);
  };

  const onTabChange = (tabIndex, { historyMode = "push" } = {}) => {
    const requestId = ++tabRequestRef.current;
    // Track tab change
    trackEvent("Print Size: Tab Change", {
      tab: getTabNameFromIndex(tabIndex),
      tab_index: tabIndex,
    });

    if (!shouldFetchTabSizes(tabIndex)) {
      const nextRatio =
        customSizeValidation.valid && customSizeValidation.ratio
          ? customSizeValidation.ratio
          : state.ratio || props.source_ratio;

      updateState(
        buildRatioSelectionState({
          cropperReady: state.cropperReady,
          ratio: nextRatio,
          updates: {
            loading: false,
            tabIndex,
          },
        }),
      );
      updateURLWithTab(tabIndex, historyMode);
      return;
    }

    const handler =
      tabIndex === PRINT_TAB_INDEXES.original ? saveOriginalSizes : saveSizes;
    const activePaperTypeId = state.paper_type_id || props.paper_type_id;

    updateState({
      loading: true,
      tabIndex,
    });

    // Update URL with the selected tab
    updateURLWithTab(tabIndex, historyMode);

    const cacheKey = `${props.image.key}:${activePaperTypeId || "default"}:${state.tabURLS[tabIndex]}`;
    if (tabCacheRef.current.has(cacheKey)) {
      handler(tabCacheRef.current.get(cacheKey));
      return;
    }

    axios({
      method: "GET",
      url: state.tabURLS[tabIndex],
      params: {
        image_key: props.image.key,
        paper_type_id: activePaperTypeId,
      },
    }).then((response) => {
      tabCacheRef.current.set(cacheKey, response.data);
      if (requestId === tabRequestRef.current) handler(response.data);
    });
  };
  onTabChangeRef.current = onTabChange;

  const handleOrientationChange = () => {
    trackEvent("Print Size: Orientation Change");

    const nextPortrait = !state.cropOrientationPortrait;
    const ps = state.print_size;
    const ratio = ps
      ? nextPortrait
        ? ps.long / ps.short
        : ps.short / ps.long
      : state.ratio
        ? 1 / state.ratio
        : state.ratio;

    updateState(
      buildRatioSelectionState({
        cropperReady: state.cropperReady,
        ratio,
        updates: {
          cropOrientationPortrait: nextPortrait,
          zoom: 1,
          crop: getDefaultCrop(),
        },
      }),
    );
  };

  const changeZoom = (val) => {
    const sanitizedZoom = isNaN(val) ? 1 : Math.max(1, Math.min(3, val));
    updateState({ zoom: sanitizedZoom });
    markCropInteracting();
    trackCropAdjusted("slider", "page");

    // Track zoom only once per zoom interaction
    if (!hasTrackedZoomRef.current) {
      trackEvent("Print Size: Zoom Control", { zoom: sanitizedZoom });
      hasTrackedZoomRef.current = true;
    }

    // Reset tracking after a short delay
    setTimeout(() => {
      hasTrackedZoomRef.current = false;
    }, 1000);
  };

  // Add missing openCropModal function
  const openCropModal = () => {
    updateState({ cropModalOpen: true });
    cropModalOpenedRef.current = true;
    trackEvent("Print Size: Crop Modal Opened", {
      image_id: props.image?.id,
      ...activeSizeProps(),
      dpi_state: state.dpiState,
      is_mobile: isMobile,
    });
  };

  const closeCropModal = () => {
    updateState({ cropModalOpen: false });
  };

  const handleModalCropChange = (crop) => {
    const sanitizedCrop = sanitizeCrop(crop);
    updateState({ crop: sanitizedCrop });
    if (cropGestureRef.current) trackCropAdjusted("drag", "modal");
  };

  const handleModalZoomChange = (zoom) => {
    const sanitizedZoom = isNaN(zoom) ? 1 : Math.max(1, Math.min(3, zoom));
    updateState({ zoom: sanitizedZoom });
    trackCropAdjusted(cropGestureRef.current ? "pinch" : "slider", "modal");
  };

  const handleModalCropComplete = (croppedArea, croppedAreaPixels) => {
    const sanitizedCroppedArea = sanitizeCroppedArea(croppedArea);
    const sanitizedCroppedAreaPixels =
      sanitizeCroppedAreaPixels(croppedAreaPixels);
    if (sanitizedCroppedArea || sanitizedCroppedAreaPixels) {
      updateState({
        ...(sanitizedCroppedArea ? { croppedArea: sanitizedCroppedArea } : {}),
        ...(sanitizedCroppedAreaPixels
          ? { croppedAreaPixels: sanitizedCroppedAreaPixels }
          : {}),
      });
    }
  };

  const handleModalMediaLoaded = useCallback(
    (mediaSize) => {
      const resolvedPreviewSize = resolvePreviewSize({
        previewWidth: mediaSize.naturalWidth || mediaSize.width,
        previewHeight: mediaSize.naturalHeight || mediaSize.height,
        imageWidth: props.image?.width,
        imageHeight: props.image?.height,
      });

      updateState({
        preview_width: resolvedPreviewSize.previewWidth,
        preview_height: resolvedPreviewSize.previewHeight,
      });
    },
    [props.image?.height, props.image?.width],
  );

  const {
    print_size,
    cropOrientationPortrait,
    zoom,
    ratio,
    crop,
    croppedAreaPixels,
    dpi,
    sizes,
    loading,
    lowDPI,
    dpiState,
  } = state;

  const { preview_url } = props;
  const committedEffectsSummary = effectsSummary(committedEffects);
  const previewCropTransform = currentCropTransform();
  const cropperImageUrl = buildEffectsUrl(committedEffects);

  const compareOriginalUrl = previewCropTransform
    ? buildEffectsUrl(
        { filter: null, viesus_correct: false },
        previewCropTransform,
      )
    : props.image?.original_cropped || preview_url;
  const compareEnhancedUrl = previewCropTransform
    ? buildEffectsUrl(draftEffects, previewCropTransform)
    : cropperImageUrl;

  return (
    <>
      <style>
        {`
          .reactEasyCrop_CropArea {
            color: rgb(230 225 217 / 80%) !important;
          }
          .removeTouchAction {
            touch-action: none;
            pointer-events: none;
          }

          .compare-label {
            background-color: rgba(255, 255, 255, 0.9) !important;
            color: #000 !important;
            border-radius: 3px !important;
          }

          .print-size-layout {
            min-height: calc(100vh - 120px);
          }

          @media (min-width: 768px) {
            .print-size-layout {
              display: grid;
              grid-template-columns: 1fr 1fr;
              min-height: calc(100vh - 120px);
            }

            .print-size-form {
              order: 1;
            }

            .print-size-left {
              order: 2;
            }
          }

          .print-size-left {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 16px 0;
            background-color: ${WARM_PLASTER};
          }

          @media (min-width: 768px) {
            .print-size-left {
              position: sticky;
              top: 0;
              height: calc(100vh - 120px);
              overflow: hidden;
              background-color: ${WARM_PLASTER};
            }
          }

          .print-size-cropper-container {
            width: 100%;
            height: min(
              90vw,
              calc(min(100vw - 32px, 640px) / var(--crop-ratio, 1) + 24px)
            );
            max-width: 640px;
            position: relative;
            background-color: ${WARM_PLASTER};
          }

          @media (min-width: 768px) {
            .print-size-cropper-container {
              height: calc(100vh - 340px);
            }

            .print-size-left .rc-slider-rail {
              background-color: rgba(0, 0, 0, 0.2);
            }
            .print-size-left .rc-slider-track {
              background-color: #1f2937;
            }
            .print-size-left .rc-slider-handle {
              border-color: #1f2937;
              background-color: #fff;
            }
          }
        `}
      </style>
      <div className="print-size-layout">
        <div
          className="print-size-left"
          style={
            effectsAccordionOpen ? { backgroundColor: CHARCOAL } : undefined
          }
        >
          {effectsAccordionOpen ? (
            <div className="w-full h-full flex items-center justify-center px-6 py-[15px] md:py-0">
              <EffectsPreviewStage
                compareOriginalUrl={compareOriginalUrl}
                compareEnhancedUrl={compareEnhancedUrl}
                effects={draftEffects}
                imageLoaded={effectsImageLoaded}
                onImageLoad={handleEffectsImageLoaded}
              />
            </div>
          ) : (
            <div className="w-full flex flex-col items-center justify-center px-4 md:px-8">
              <div
                className="print-size-cropper-container"
                style={ratio > 0 ? { "--crop-ratio": ratio } : undefined}
              >
                {cropperImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center z-20 bg-transparent">
                    <div className="w-10 h-10 border-4 border-stone-800/20 border-t-stone-800 rounded-full animate-spin"></div>
                  </div>
                )}
                <Cropper
                  key={cropperImageUrl}
                  image={cropperImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={ratio}
                  onCropChange={onCropChange}
                  onZoomChange={onZoomChange}
                  onInteractionStart={onCropInteractionStart}
                  onInteractionEnd={onCropInteractionEnd}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                  onTouchRequest={() => !isMobile}
                  showGrid={isCropInteracting}
                  zoomWithScroll={false}
                  cropShape="rect"
                  objectFit="contain"
                  classes={
                    isMobile && { containerClassName: "removeTouchAction" }
                  }
                />

                {dpiState === "too-low" && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-sm text-center p-4 bg-white border border-black z-10 shadow-lg">
                    <div className="text-lg font-bold mb-4">Oh, snap.</div>
                    <div className="mb-2 text-sm">
                      Image resolution is too low at this zoom level and print
                      size.
                    </div>
                    <div className="mb-2 text-sm">
                      Zoom out or choose a smaller print size.
                    </div>
                  </div>
                )}
              </div>

              {isMobile && (
                <button
                  onClick={openCropModal}
                  className="btn btn-secondary mt-3 px-5 py-2.5 bg-white text-black mini-heading rounded-none border border-black"
                >
                  <i className="fa-solid fa-crop-simple mr-2"></i>
                  Edit crop
                </button>
              )}

              {!isMobile && !effectsAccordionOpen && (
                <div className="w-full max-w-md mt-4 px-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const newZoom = Math.max(1, zoom - 0.1);
                        changeZoom(newZoom);
                      }}
                      className="text-gray-400 hover:text-white md:text-gray-800 md:hover:text-gray-950 mr-5 cursor-pointer"
                      disabled={zoom <= 1}
                    >
                      <i className="fa-solid fa-minus"></i>
                    </button>
                    <Slider
                      min={1}
                      max={3}
                      step={0.01}
                      value={zoom}
                      onChange={changeZoom}
                      className="flex-1"
                    />
                    <button
                      onClick={() => {
                        const newZoom = Math.min(3, zoom + 0.1);
                        changeZoom(newZoom);
                      }}
                      className="text-gray-400 hover:text-white md:text-gray-800 md:hover:text-gray-950 ml-3 cursor-pointer"
                      disabled={zoom >= 3}
                    >
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </div>
                </div>
              )}

              {!effectsAccordionOpen && hasResolutionContext && (
                <>
                  {props.media_type !== "print_only" && (
                    <div className="mt-4">
                      <div className="text-center">
                        <div className="mini-heading text-stone-800 mb-2">
                          {dpiState === "great" && (
                            <div className="flex items-center justify-center gap-2">
                              <ThumbsIcon />
                              <span>Image Resolution: Great</span>
                            </div>
                          )}
                          {dpiState === "ok" && (
                            <div className="flex items-center justify-center gap-2 text-orange-600">
                              <ThumbsIcon flipped rotate />
                              <span>Image Resolution: OK</span>
                            </div>
                          )}
                          {dpiState === "too-low" && (
                            <div className="flex items-center justify-center gap-2 text-red-600">
                              <ThumbsIcon flipped />
                              <span>Image Resolution: Too Low</span>
                            </div>
                          )}
                        </div>
                        {dpiState !== "great" && !isMobile && (
                          <ImproveResolutionHint className="text-sm" />
                        )}
                        {state.tabIndex === PRINT_TAB_INDEXES.recommended &&
                          print_size &&
                          !isMobile && (
                            <div className="mt-2">
                              <OrientationLink
                                handleOrientationChange={
                                  handleOrientationChange
                                }
                                label={
                                  (
                                    cropOrientationPortrait !== null
                                      ? cropOrientationPortrait
                                      : props.source_ratio > 1
                                  )
                                    ? "Landscape"
                                    : "Portrait"
                                }
                              />
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="print-size-form px-4 py-5 md:py-10 md:px-10 lg:px-14 md:overflow-y-auto md:max-w-2xl md:w-full md:justify-self-end">
          {sizes || loading || isCustomTab ? (
            <SelectContainer
              {...state}
              {...props}
              image_key={props.image.key}
              customSize={customSize}
              customSizeValidation={customSizeValidation}
              customTabAllowed={customTabAllowed}
              handleCustomSizeChange={handleCustomSizeChange}
              flipCustomSize={flipCustomSize}
              isCustomTab={isCustomTab}
              validateAndFrame={validateAndFrame}
              sizeSelected={sizeSelected}
              handleOrientationChange={handleOrientationChange}
              onTabChange={onTabChange}
              effectsAccordionOpen={effectsAccordionOpen}
              committedEffectsSummary={committedEffectsSummary}
              draftEffects={draftEffects}
              openEffectsAccordion={openEffectsAccordion}
              cancelEffectsEditing={cancelEffectsEditing}
              doneEffectsEditing={doneEffectsEditing}
              setDraftEffects={setDraftEffects}
              canOfferUpscale={showUpscaleOffer}
              onUpscalePrompt={() => openUpscaleModal("unlock_prompt")}
            />
          ) : (
            <BlankState />
          )}
        </div>

        {/* Crop editing modal for mobile */}
        <Modal
          open={state.cropModalOpen}
          onClose={closeCropModal}
          animationDuration={isMobile ? 0 : 300}
          classNames={{
            overlay: "custom-overlay",
            modal: "custom-modal crop-modal w-full h-full !m-0",
          }}
        >
          <div className="modal-scroll h-full flex flex-col">
            <div className="mb-4">
              <h1 className="text-3xl md:text-3xl tracking-tight mt-0">
                Crop your image
              </h1>
            </div>

            <div className="flex-grow overflow-auto">
              <div
                className="relative"
                style={{
                  height: "400px",
                  maxWidth: "500px",
                  margin: "0 auto",
                  backgroundColor: WARM_PLASTER,
                }}
              >
                <Cropper
                  image={cropperImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={ratio}
                  onCropChange={handleModalCropChange}
                  onZoomChange={handleModalZoomChange}
                  onInteractionStart={onCropInteractionStart}
                  onInteractionEnd={onCropInteractionEnd}
                  onCropComplete={handleModalCropComplete}
                  onMediaLoaded={handleModalMediaLoaded}
                  showGrid={true}
                  zoomWithScroll={false}
                  cropShape="rect"
                  objectFit="contain"
                />
              </div>

              <div className="mt-4">
                <div className="flex items-center gap-3 max-w-md mx-auto">
                  <button
                    onClick={() => {
                      const newZoom = Math.max(1, zoom - 0.1);
                      handleModalZoomChange(newZoom);
                    }}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                    disabled={zoom <= 1}
                  >
                    <i className="fa-solid fa-minus"></i>
                  </button>
                  <Slider
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={handleModalZoomChange}
                    className="flex-1"
                  />
                  <button
                    onClick={() => {
                      const newZoom = Math.min(3, zoom + 0.1);
                      handleModalZoomChange(newZoom);
                    }}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                    disabled={zoom >= 3}
                  >
                    <i className="fa-solid fa-plus"></i>
                  </button>
                </div>

                {hasResolutionContext && (
                  <>
                    {props.media_type !== "print_only" && (
                      <div className="mt-5">
                        <div className="text-center">
                          <div className="mini-heading text-black mb-4">
                            {dpiState === "great" && (
                              <div className="flex items-center justify-center gap-2">
                                <ThumbsIcon />
                                <span>Image Resolution: Great</span>
                              </div>
                            )}
                            {dpiState === "ok" && (
                              <div className="flex items-center justify-center gap-2">
                                <ThumbsIcon flipped rotate />
                                <span>Image Resolution: OK</span>
                              </div>
                            )}
                            {dpiState === "too-low" && (
                              <div className="flex items-center justify-center gap-2 text-red-700">
                                <ThumbsIcon flipped />
                                <span>Image Resolution: Too Low</span>
                              </div>
                            )}
                          </div>
                          {dpiState !== "great" && (
                            <ImproveResolutionHint className="text-base" />
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="text-center py-6mt-auto flex-shrink-0">
              <button
                onClick={closeCropModal}
                className="btn btn-primary btn-lg block w-full mr-2 disabled:bg-stone-400 disabled:border-stone-400 disabled:text-white disabled:cursor-not-allowed"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      </div>

      {showUpscaleOffer && (
        <CustomerUpscale
          upscale_available={props.upscale_available}
          frame_upscale_path={props.frame_upscale_path}
          media_type={props.media_type}
          image={props.image}
          ai_icon_path={props.ai_icon_path}
          ai_icon_loading_path={props.ai_icon_loading_path}
          open={upscaleModal.open}
          source={upscaleModal.source}
          onOpenChange={(open) =>
            setUpscaleModal((current) => ({ ...current, open }))
          }
          showTrigger={false}
        />
      )}
    </>
  );
};

const ImproveResolutionHint = ({ className }) => (
  <div
    className={classNames(
      "mx-auto max-w-lg px-1 text-center leading-relaxed text-gray-900",
      className,
    )}
  >
    To improve resolution, try a smaller print size or{" "}
    <a
      className="font-medium text-gray-950 underline decoration-gray-900/45 underline-offset-[3px] transition-colors hover:decoration-gray-900"
      href="/apps/frame/digital-upload"
    >
      upload a higher resolution
    </a>
    .
  </div>
);

const CompareImage = ({ src, classAdd, onLoad, labelText }) => {
  return (
    <div
      className="compare-image-container"
      style={{ width: "100%", height: "100%" }}
    >
      <div className={`compare-label ${classAdd}`}>{labelText}</div>
      <img
        src={src}
        className="compare-image"
        onLoad={onLoad}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </div>
  );
};

const SelectContainer = (props) => {
  const selector = (
    <PrintSizeSelect
      is_pro={props.is_pro}
      tax_rate={props.tax_rate}
      sizes={props.sizes}
      print_size={props.print_size}
      print_price={props.print_price}
      onSelect={props.sizeSelected}
      loading={props.loading}
      max_glass_short_mm={props.max_glass_short_mm}
      max_glass_long_mm={props.max_glass_long_mm}
      tabIndex={props.tabIndex}
      media_type={props.media_type}
      country_name={props.country_name}
    />
  );

  if (props.effectsAccordionOpen) {
    const size = props.print_size;
    const sizeLabel =
      !props.isCustomTab && size
        ? `${size.title}, ${size.long_mm} × ${size.short_mm} mm`
        : null;

    return (
      <FocusedEffectsEditor
        draftEffects={props.draftEffects}
        onEffectsChange={props.setDraftEffects}
        onCancel={props.cancelEffectsEditing}
        onDone={props.doneEffectsEditing}
        url={props.image?.original_url}
        isPro={props.is_pro}
        sizeLabel={sizeLabel}
      />
    );
  }

  const isDisabled = isContinueDisabled({
    tabIndex: props.tabIndex,
    loading: props.loading,
    dpiState: props.dpiState,
    cropperReady: props.cropperReady,
    saving: props.saving,
    ratio: props.ratio,
    printSize: props.print_size,
    effectsAccordionOpen: props.effectsAccordionOpen,
    customSizeValid: props.customSizeValidation.valid,
  });

  return (
    <div className="">
      <h1 className="text-3xl md:text-4xl md:mb-6 mb-3">
        02 — Select a print size
      </h1>

      <div className="pt-3">
        <div className="row">
          <div className="col-sm-24">
            <Tabs
              onSelect={(tabIndex) => props.onTabChange(tabIndex)}
              selectedIndex={props.tabIndex}
            >
              <div>
                <TabList>
                  <Tab>
                    Popular<span className="hidden md:inline"> Sizes</span>
                  </Tab>
                  <Tab>Square</Tab>
                  <Tab>
                    Original<span className="hidden md:inline"> Ratio</span>
                  </Tab>
                  {props.customTabAllowed && (
                    <Tab>
                      Custom <i className="fa-solid fa-rectangle-pro"></i>
                    </Tab>
                  )}
                </TabList>
              </div>
              <TabPanel>{selector}</TabPanel>
              <TabPanel>{selector}</TabPanel>
              <TabPanel>
                <p>
                  These sizes preserve the original aspect ratio of your image.
                </p>
                {selector}
              </TabPanel>
              {props.customTabAllowed && (
                <TabPanel>
                  <CustomPrintSizePanel
                    customSize={props.customSize}
                    customSizeValidation={props.customSizeValidation}
                    handleCustomSizeChange={props.handleCustomSizeChange}
                  />
                </TabPanel>
              )}
            </Tabs>
          </div>
        </div>
      </div>

      {props.image?.ai_upscaled ? (
        <UpscaledNotice />
      ) : (
        props.canOfferUpscale &&
        !props.loading && (
          <UpscaleUnlockPrompt
            sizes={props.isCustomTab ? [] : props.sizes}
            onUpscale={props.onUpscalePrompt}
          />
        )
      )}

      <AdjustLookRow
        summary={props.committedEffectsSummary}
        onOpen={props.openEffectsAccordion}
      />

      <div className="sticky bottom-0 z-10 -mx-4 flex items-center gap-5 border-t border-stone-200 bg-white px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:static md:mx-0 md:block md:border-0 md:p-0">
        {!props.isCustomTab && props.print_size && (
          <div className="smallish flex-shrink-0">
            <strong className="flex flex-col md:block md:mt-2 md:mb-3">
              <span className="text-xs font-medium text-stone-500 md:text-[1em] md:font-bold md:text-stone-900">
                From{" "}
              </span>

              <span className="whitespace-nowrap text-2xl leading-tight md:text-4xl font-normal tracking-tight">
                <CurrencyDisplay value={props.print_size.frame_price_no_mat} />
                {props.is_pro && (
                  <i className="fa-solid fa-rectangle-pro ml-1.5 text-sm align-middle md:ml-2 md:text-base"></i>
                )}
              </span>
            </strong>
          </div>
        )}

        <div className="min-w-0 flex-1 text-center md:text-left md:mt-2">
          <button
            onClick={props.validateAndFrame}
            disabled={isDisabled}
            className="btn btn-primary btn-lg block w-full mr-2 whitespace-nowrap px-4 py-3.5 text-base md:px-7 md:py-4 md:text-lg disabled:bg-stone-400 disabled:border-stone-400 disabled:text-white disabled:cursor-not-allowed"
          >
            {props.media_type === "print_only"
              ? "Next: Choose paper type"
              : "Next: Choose frame"}
            {props.saving || props.loading ? (
              <i className="fa-solid fa-spinner-third fa-spin ml-2"></i>
            ) : (
              <i className="fa-solid fa-arrow-right-long ml-2 text-base" />
            )}
          </button>
        </div>
      </div>
      <div className="text-sm mt-5">
        <ShippingEstimate
          shipping_icon={props.shipping_icon}
          shipping_estimates={props.shipping_estimates}
          print_and_frame={true}
          country={props.country_name}
        />
      </div>
    </div>
  );
};

const CustomPrintSizePanel = ({
  customSize,
  customSizeValidation,
  handleCustomSizeChange,
}) => {
  const showValidationError =
    !customSizeValidation.valid &&
    !!customSizeValidation.message &&
    (customSize.width !== "" || customSize.height !== "");

  const unitLabel = customSize.unit || "mm";

  return (
    <div className="space-y-4 border border-black bg-stone-50 p-4 pb-5">
      <div>
        <h3 className="text-lg font-medium text-stone-900 mb-1">
          Custom Print Size
          <i className="fa-solid fa-rectangle-pro ml-2"></i>
        </h3>
        <p className="text-sm text-stone-700 mb-1">
          Enter an exact print size. For pricing this will be matched to the
          nearest standard print size.
        </p>
        <p className="text-xs text-stone-500">
          {customSizeValidation.maxShortMm != null
            ? `Custom sizes support up to ${customSizeValidation.maxMm}mm x ${customSizeValidation.maxShortMm}mm (min ${customSizeValidation.minMm}mm per side) in mm, cm, or inches.`
            : `Custom sizes support ${customSizeValidation.minMm}mm to ${customSizeValidation.maxMm}mm per side in mm, cm, or inches.`}
        </p>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-3">
        <fieldset className="relative rounded border border-stone-900 bg-white px-3 pt-1 pb-2">
          <legend className="px-1 text-xs text-stone-500">{`Width (${unitLabel})`}</legend>
          <input
            type="number"
            min="0"
            step="0.1"
            value={customSize.width}
            onChange={(event) =>
              handleCustomSizeChange("width", event.target.value)
            }
            className="w-full border-none bg-transparent text-base text-stone-900 shadow-none focus:outline-none focus:ring-0"
          />
        </fieldset>

        <span className="text-xl font-medium text-stone-900">&times;</span>

        <fieldset className="relative rounded border border-stone-900 bg-white px-3 pt-1 pb-2">
          <legend className="px-1 text-xs text-stone-500">{`Height (${unitLabel})`}</legend>
          <input
            type="number"
            min="0"
            step="0.1"
            value={customSize.height}
            onChange={(event) =>
              handleCustomSizeChange("height", event.target.value)
            }
            className="w-full border-none bg-transparent text-base text-stone-900 shadow-none focus:outline-none focus:ring-0"
          />
        </fieldset>

        <div className="flex gap-1">
          {CUSTOM_SIZE_UNIT_OPTIONS.map((option) => {
            const active = customSize.unit === option.title;

            return (
              <button
                key={option.title}
                type="button"
                onClick={() => handleCustomSizeChange("unit", option.title)}
                className={classNames(
                  "rounded px-3 py-2 text-sm font-medium transition-colors",
                  active
                    ? "bg-stone-900 text-white"
                    : "bg-stone-200 text-stone-600 hover:bg-stone-300",
                )}
              >
                {option.title}
              </button>
            );
          })}
        </div>
      </div>

      {showValidationError && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {customSizeValidation.message}
        </div>
      )}

      {customSize.matchError && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {customSize.matchError}
        </div>
      )}
    </div>
  );
};

const OrientationLink = ({ handleOrientationChange, label }) => (
  <button
    type="button"
    onClick={handleOrientationChange}
    className="mini-heading text-stone-800 inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-70"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 640 640"
      className="w-4 h-4"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M544.1 256L552 256C565.3 256 576 245.3 576 232L576 88C576 78.3 570.2 69.5 561.2 65.8C552.2 62.1 541.9 64.2 535 71L483.3 122.8C439 86.1 382 64 320 64C191 64 84.3 159.4 66.6 283.5C64.1 301 76.2 317.2 93.7 319.7C111.2 322.2 127.4 310 129.9 292.6C143.2 199.5 223.3 128 320 128C364.4 128 405.2 143 437.7 168.3L391 215C384.1 221.9 382.1 232.2 385.8 241.2C389.5 250.2 398.3 256 408 256L544.1 256zM573.5 356.5C576 339 563.8 322.8 546.4 320.3C529 317.8 512.7 330 510.2 347.4C496.9 440.4 416.8 511.9 320.1 511.9C275.7 511.9 234.9 496.9 202.4 471.6L249 425C255.9 418.1 257.9 407.8 254.2 398.8C250.5 389.8 241.7 384 232 384L88 384C74.7 384 64 394.7 64 408L64 552C64 561.7 69.8 570.5 78.8 574.2C87.8 577.9 98.1 575.8 105 569L156.8 517.2C201 553.9 258 576 320 576C449 576 555.7 480.6 573.4 356.5z" />
    </svg>
    <span>{label}</span>
  </button>
);

const BlankState = () => {
  return (
    <div className="alert alert-warning mt-2 py-3">
      <h4>Whoops 😕</h4>
      <span>
        This image isn't a high enough resolution for us to print. Please try
        another file and avoid cropping too tightly.
      </span>
      <p className="text-muted small mt-1">
        Please feel free to <a href="/pages/contact">contact us</a> if you're
        having problems.
      </p>
    </div>
  );
};

const UpscaleUnlockPrompt = ({ sizes, onUpscale }) => {
  const copy = upscaleUnlockCopy(sizes);

  return (
    <div
      data-upscale-unlock-prompt
      className="md:mt-1 mb-6 flex flex-col gap-3 bg-[#F3F2F0] px-4 py-3.5 md:flex-row md:items-center"
    >
      <div className="flex flex-1 items-start gap-3">
        <i
          className="fa-solid fa-up-right-and-down-left-from-center mt-0.5 text-base text-stone-800 flex-shrink-0"
          aria-hidden="true"
        ></i>
        <div>
          <div className="text-sm font-semibold text-stone-900">
            {copy.title}
          </div>
          <div className="text-xs text-stone-600 mt-0.5">{copy.subtitle}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onUpscale}
        className="w-full md:w-auto flex-shrink-0 border border-stone-900 bg-white px-4 py-2 text-sm font-semibold text-stone-900 hover:bg-stone-900 hover:text-white transition-colors"
      >
        Upscale photo
      </button>
    </div>
  );
};

const UpscaledNotice = () => (
  <div
    data-upscaled-notice
    role="status"
    className="md:mt-1 mb-6 flex items-start gap-3 bg-[#F3F2F0] px-4 py-3.5"
  >
    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-700 text-white">
      <i className="fa-solid fa-check text-[10px]" aria-hidden="true"></i>
    </span>
    <div>
      <div className="text-sm font-semibold text-stone-900">
        Your photo has been AI upscaled
      </div>
      <div className="text-xs text-stone-600 mt-0.5">
        Larger print sizes are now available
      </div>
    </div>
  </div>
);

export const effectsSummary = (effects) => {
  const enhance = `Smart Enhance is ${effects?.viesus_correct ? "on" : "off"}`;
  if (effects?.filter) {
    return `${enhance}. ${capitalise(effects.filter)} filter applied.`;
  }
  return `Colour, light and filters. ${enhance}.`;
};

const capitalise = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const AdjustLookRow = ({ summary, onOpen }) => (
  <div className="mb-6">
    <div className="mini-heading text-stone-500 mb-2">Adjust the look</div>
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-center gap-3 border-y border-stone-200 py-3 text-left hover:bg-stone-50"
    >
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F3F2F0]">
        <i
          className="fa-solid fa-sliders text-sm text-stone-800"
          aria-hidden="true"
        ></i>
      </span>
      <span className="flex flex-1 flex-col">
        <span className="text-sm font-semibold text-stone-900">
          Enhance &amp; Filter
        </span>
        <span className="text-xs text-stone-500">{summary}</span>
      </span>
      <span className="text-xs underline text-stone-700">Edit</span>
    </button>
  </div>
);

const FilterThumb = ({ url, filter }) => {
  const src = useMemo(() => {
    if (!url) return null;
    const cl = new cloudinary.Cloudinary({
      cloud_name: "framefox",
      secure: true,
    });
    const transformation = [{ width: 160, height: 160, crop: "fill" }];
    if (filter) transformation.push({ effect: `art:${filter}` });
    return cl.url(url, { transformation });
  }, [url, filter]);

  const [loadedSrc, setLoadedSrc] = useState(null);
  const loaded = !!src && loadedSrc === src;
  const setLoaded = () => setLoadedSrc(src);

  return (
    <span
      className="relative block w-full bg-stone-100"
      style={{ paddingBottom: "100%" }}
    >
      {src && (
        <img
          src={src}
          alt=""
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          className={classNames(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-200",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
      {!loaded && (
        <span
          role="status"
          aria-label="Loading filter preview"
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-stone-300 border-t-stone-700" />
        </span>
      )}
    </span>
  );
};

const FocusedEffectsEditor = ({
  draftEffects,
  onEffectsChange,
  onCancel,
  onDone,
  url,
  isPro,
  sizeLabel,
}) => {
  const smartEnhanceOn = !!draftEffects.viesus_correct;
  const setFilter = (filter) => onEffectsChange({ ...draftEffects, filter });
  const toggleSmartEnhance = () =>
    onEffectsChange({ ...draftEffects, viesus_correct: !smartEnhanceOn });
  const options = [null, ...EFFECT_FILTERS];

  return (
    <div data-focused-effects-editor className="pb-2">
      <button
        type="button"
        onClick={onCancel}
        className="mb-6 inline-flex items-center gap-2 text-sm text-stone-700 hover:text-stone-900"
      >
        <i className="fa-solid fa-arrow-left-long" aria-hidden="true"></i>
        <span>
          Back to print size
          {sizeLabel && <span className="text-stone-500"> · {sizeLabel}</span>}
        </span>
      </button>

      <div className="mini-heading text-stone-500 mb-5 md:mb-2">
        Enhance &amp; Filter
      </div>
      <h1
        className={classNames(
          "hidden md:block text-4xl",
          isPro ? "md:mb-4" : "md:mb-9",
        )}
      >
        Change the look
      </h1>

      {isPro && (
        <div className="mb-8 rounded-md bg-stone-100 p-3">
          <div className="text-sm font-semibold text-stone-900">
            <i className="fa-solid fa-rectangle-pro mr-1.5"></i>
            Pro Customers
          </div>
          <p className="mb-0 mt-1 text-xs text-stone-600">
            We recommend using your own Photoshop or Lightroom workflow to
            enhance your images for precise control.
          </p>
        </div>
      )}

      <div className="flex items-center gap-4 border-b border-stone-200 pb-4">
        <div className="flex-1">
          <div className="text-sm font-semibold text-stone-900">
            Smart Enhance
          </div>
          <div className="text-xs text-stone-500">
            One tap fix for brightness, colour, sharpness and noise.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={smartEnhanceOn}
          aria-label="Smart Enhance"
          onClick={toggleSmartEnhance}
          className={classNames(
            "relative h-6 w-11 flex-shrink-0 rounded-full transition-colors",
            smartEnhanceOn ? "bg-stone-900" : "bg-stone-300",
          )}
        >
          <span
            className={classNames(
              "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
              smartEnhanceOn ? "translate-x-5" : "translate-x-0",
            )}
          />
        </button>
      </div>

      <div className="py-4">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-sm font-semibold text-stone-900">Filter</span>
          <span className="text-xs text-stone-500 md:hidden">
            Swipe for more
          </span>
        </div>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-6 md:overflow-visible md:px-0">
          {options.map((filter) => {
            const selected = (draftEffects.filter || null) === filter;
            return (
              <button
                key={filter || "none"}
                type="button"
                aria-pressed={selected}
                onClick={() => setFilter(filter)}
                className="w-20 flex-shrink-0 text-left md:w-auto"
              >
                <span
                  className={classNames(
                    "block overflow-hidden border-2",
                    selected ? "border-stone-900" : "border-transparent",
                  )}
                >
                  <FilterThumb url={url} filter={filter} />
                </span>
                <span
                  className={classNames(
                    "mt-1 block text-xs",
                    selected
                      ? "font-semibold text-stone-900"
                      : "text-stone-600",
                  )}
                >
                  {filter ? capitalise(filter) : "Original"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-0 z-10 -mx-4 flex gap-3 border-t border-stone-200 bg-white px-4 py-3 md:static md:mx-0 md:block md:border-0 md:px-0 md:py-0 md:mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary flex flex-1 items-center justify-center whitespace-nowrap px-4 text-center md:hidden"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onDone}
          className="btn btn-primary flex flex-[2] items-center justify-center whitespace-nowrap px-4 text-center md:w-full md:py-4 md:text-lg"
        >
          Apply changes
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="hidden md:block mx-auto mt-3 text-sm underline text-stone-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const EffectsPreviewStage = ({
  compareOriginalUrl,
  compareEnhancedUrl,
  effects,
  imageLoaded,
  onImageLoad,
}) => {
  const containerRef = useRef(null);
  const [sliderSize, setSliderSize] = useState(null);

  const computeSize = useCallback((naturalWidth, naturalHeight) => {
    const container = containerRef.current;
    if (!container || !naturalWidth || !naturalHeight) return;

    const maxW = container.clientWidth;
    const maxH = container.clientHeight;
    const imgRatio = naturalWidth / naturalHeight;
    const boxRatio = maxW / maxH;

    let w, h;
    if (imgRatio > boxRatio) {
      w = maxW;
      h = maxW / imgRatio;
    } else {
      h = maxH;
      w = maxH * imgRatio;
    }

    setSliderSize({ width: Math.round(w), height: Math.round(h) });
  }, []);

  const handleOriginalLoad = useCallback(
    (e) => {
      const img = e.target;
      computeSize(img.naturalWidth, img.naturalHeight);
      onImageLoad("original");
    },
    [computeSize, onImageLoad],
  );

  return (
    <div
      ref={containerRef}
      className="relative w-full flex items-center justify-center"
      style={{
        maxWidth: "640px",
        height: sliderSize ? `${sliderSize.height}px` : "calc(100vh - 340px)",
        maxHeight: "calc(100vh - 340px)",
      }}
    >
      {effects.filter || effects.viesus_correct ? (
        <ReactCompareSlider
          style={{
            width: sliderSize ? `${sliderSize.width}px` : "100%",
            height: sliderSize ? `${sliderSize.height}px` : "100%",
          }}
          itemOne={
            <ComparePreviewImage
              labelText="Original"
              src={compareOriginalUrl}
              classAdd="left"
              onLoad={handleOriginalLoad}
            />
          }
          itemTwo={
            <ComparePreviewImage
              labelText={
                effects.filter && effects.viesus_correct
                  ? "Enhanced + Filter"
                  : effects.filter
                    ? "Filtered"
                    : "Smart Enhance"
              }
              src={compareEnhancedUrl}
              classAdd="right"
              onLoad={() => onImageLoad("enhanced")}
            />
          }
          handle={
            <div className="flex h-full cursor-ew-resize flex-col items-center pointer-events-none text-white">
              <div className="pointer-events-auto h-full w-0.5 flex-grow bg-current shadow-[0_0_4px_rgba(0,0,0,0.5)]"></div>
              <div className="grid h-14 w-14 flex-shrink-0 grid-flow-col place-content-center gap-2 rounded-full border-2 border-white/80 bg-black/15 backdrop-blur-[7px] pointer-events-auto shadow-[0_0_4px_rgba(0,0,0,0.35)]">
                {!imageLoaded ? (
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                ) : (
                  <>
                    <div className="h-0 w-0 border-b-[8px] border-r-[10px] border-t-[8px] border-b-transparent border-r-current border-t-transparent"></div>
                    <div className="h-0 w-0 rotate-180 border-b-[8px] border-r-[10px] border-t-[8px] border-b-transparent border-r-current border-t-transparent"></div>
                  </>
                )}
              </div>
              <div className="pointer-events-auto h-full w-0.5 flex-grow bg-current shadow-[0_0_4px_rgba(0,0,0,0.5)]"></div>
            </div>
          }
        />
      ) : (
        <img
          src={compareOriginalUrl}
          className="compare-image"
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          onLoad={handleOriginalLoad}
          alt="Original image"
        />
      )}
    </div>
  );
};

const ComparePreviewImage = ({ src, classAdd, onLoad, labelText }) => (
  <div style={{ width: "100%", height: "100%" }}>
    <div className={`compare-label ${classAdd}`}>{labelText}</div>
    <img
      src={src}
      className="compare-image"
      onLoad={onLoad}
      alt={labelText}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      }}
    />
  </div>
);

const DPIControl = ({ dpi }) => {
  return (
    <div className="mini-heading text-dark mt-3">
      Print resolution {dpi} DPI · <DPIWarning dpi={dpi} />
    </div>
  );
};

const DPIWarning = ({ dpi }) => {
  if (dpi <= 90) {
    return <span className="text-danger">Too Low</span>;
  } else if (dpi > 90 && dpi <= 125) {
    return <span className="text-warning">OK</span>;
  } else {
    return <span className="text-success">Great</span>;
  }
};

const PrintSizeSelect = ({
  sizes,
  onSelect,
  loading,
  print_size,
  tabIndex,
  country_name,
}) => {
  // Only hide inch sizes by default for NZ and only in the first tab (tabIndex === 0)
  const isNZ =
    country_name && country_name.toLowerCase().includes("new zealand");
  // Show inch sizes by default if not in first tab, or if not in NZ
  const [showInchSizes, setShowInchSizes] = useState(tabIndex !== 0 || !isNZ);

  // Update showInchSizes when tabIndex changes
  useEffect(() => {
    setShowInchSizes(tabIndex !== 0 || !isNZ);
  }, [tabIndex, isNZ]);

  // Split sizes into two groups
  const isoSizes = sizes?.filter((size) => size.is_a_size) || [];
  const inchSizes = sizes?.filter((size) => !size.is_a_size) || [];
  const sortSizesByOrder = (groupSizes) =>
    [...groupSizes].sort((a, b) => {
      const areaDiff = a.long_mm * a.short_mm - b.long_mm * b.short_mm;
      if (areaDiff !== 0) return areaDiff;

      const longDiff = a.long_mm - b.long_mm;
      if (longDiff !== 0) return longDiff;

      return a.short_mm - b.short_mm;
    });

  const orderedIsoSizes = sortSizesByOrder(isoSizes);
  const orderedInchSizes = sortSizesByOrder(inchSizes);
  const orderedSizes = sortSizesByOrder(sizes || []);

  const renderSizeGroup = (groupSizes, groupTitle) => {
    if (groupSizes.length === 0) return null;

    return (
      <div className="mb-2">
        {tabIndex === 0 && groupTitle && (
          <h3 className="text-xs font-semibold text-gray-900 mb-2">
            {groupTitle}
          </h3>
        )}
        <div className="">
          {groupSizes.map((option) => {
            const radioOption = (
              <RadioGroup.Option
                key={option.id}
                value={option}
                disabled={option.disabled}
                className={({ active, checked }) =>
                  classNames(
                    active && !option.disabled
                      ? "bg-stone-800 text-white hover:bg-stone-800 border-2 border-black"
                      : "",
                    checked && !option.disabled
                      ? "bg-stone-800 text-white hover:bg-stone-800 border-2 border-black"
                      : option.disabled
                        ? "ring-1 ring-inset ring-gray-100 bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-100"
                        : "bg-white text-gray-900 hover:bg-gray-50 border border-black",
                    "items-center justify-center rounded-sm mr-2 mb-2 py-2 px-[9px] text-sm font-semibold inline-block",
                    option.disabled ? "cursor-not-allowed" : "cursor-pointer",
                  )
                }
              >
                <RadioGroup.Label as="span">
                  {option.title}
                  {option.is_a_size && tabIndex !== 2 && (
                    <>
                      {" "}
                      <span className="text-xxxs ">
                        {option.long_mm}x{option.short_mm}
                      </span>
                    </>
                  )}
                  {tabIndex == 1 && option.short > 100 && (
                    <>
                      {" "}
                      <span className="text-xxxs ">
                        {option.long_mm} x {option.short_mm}
                      </span>
                    </>
                  )}
                  {tabIndex !== 2 && (
                    <div
                      className={classNames(
                        "text-xxxs",
                        option.disabled ? "text-gray-400" : "text-muted",
                      )}
                    ></div>
                  )}
                </RadioGroup.Label>
              </RadioGroup.Option>
            );

            // Wrap disabled options with tooltip
            if (option.disabled) {
              return (
                <Tooltip
                  key={option.id}
                  title="Image resolution too low"
                  arrow={true}
                  position="top"
                  trigger="mouseenter"
                  distance={25}
                >
                  {radioOption}
                </Tooltip>
              );
            }

            return radioOption;
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      {sizes && !loading ? (
        <div>
          <div className="categories-radio-buttons">
            <RadioGroup
              value={print_size}
              onChange={(e) => onSelect(e)}
              className="mt-2"
            >
              {tabIndex === 2 ? (
                renderSizeGroup(orderedSizes)
              ) : (
                <>
                  {renderSizeGroup(orderedIsoSizes)}
                  {showInchSizes && renderSizeGroup(orderedInchSizes, "Inches")}
                </>
              )}
            </RadioGroup>

            {/* Only show the "Show Inch Sizes" button in the first tab */}
            {tabIndex === 0 && !showInchSizes && inchSizes.length > 0 && (
              <button
                type="button"
                onClick={() => setShowInchSizes(true)}
                className="text-xs text-stone-700 underline underline-offset-2 hover:text-stone-900 -mt-2 mb-4"
              >
                Show inch sizes
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white p-3 mt-3">
          <div className="w-8 h-8 border-4 border-stone-300 border-t-stone-800 rounded-full animate-spin mx-auto"></div>
        </div>
      )}
    </div>
  );
};

export default PrintSizeForm;
